import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Dialog,
  DialogContent,
  Checkbox,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { ChevronLeft, Edit, Close } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { useClinic } from '../hooks/useClinic';
import { reviewService, ReviewResponse } from '../services/reviewService';
import { getUserPhotoUrl } from '../utils/getUserPhotoUrl';

// КОНСТАНТЫ
const GOOD_THRESHOLD = 4;

const NEGATIVE_REASONS = [
  'Врач показался некомпетентным',
  'Длительное время ожидания в очереди',
  'Администратор был груб со мной',
  'Неприятная атмосфера в клинике',
  'Грязный кабинет и холл',
  'Другое',
];

// КНОПКА «НАЗАД»
const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      mb: { xs: 1.5, sm: 2 },
      ml: '-4px',
      color: '#007AFF',
      cursor: 'pointer',
      userSelect: 'none',
      '&:active': { opacity: 0.6 },
    }}
  >
    <ChevronLeft sx={{ fontSize: { xs: 14, sm: 16 }, mr: '-2px', ml: '-2px', mt: '1px' }} />
    <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, lineHeight: 1 }}>Назад</Typography>
  </Box>
);

// КАРТОЧКА ПОЛЬЗОВАТЕЛЯ
const UserCard: React.FC<{
  name: string;
  branchName: string;
  onEdit: () => void;
  photoUrl?: string | null;
}> = ({ name, branchName, onEdit, photoUrl }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1, sm: 1.5 },
      backgroundColor: '#FFFFFF',
      borderRadius: '24px',
      p: { xs: 1.5, sm: 2 },
      mb: { xs: 1.5, sm: 2 },
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}
  >
    <Avatar
      src={photoUrl || undefined}
      sx={{
        width: { xs: 48, sm: 52 },
        height: { xs: 48, sm: 52 },
        bgcolor: '#1677FF',
        fontSize: { xs: 20, sm: 22 },
        fontWeight: 600,
      }}
    >
      {name?.charAt(0)?.toUpperCase() || '?'}
    </Avatar>
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 600,
            color: '#1C1C1E',
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          {name}
        </Typography>
        <IconButton
          size="small"
          onClick={onEdit}
          sx={{
            p: 0.4,
            color: '#1677FF',
            backgroundColor: 'rgba(22,119,255,0.1)',
            borderRadius: '6px',
          }}
        >
          <Edit sx={{ fontSize: { xs: 12, sm: 14 } }} />
        </IconButton>
      </Box>
      <Typography
        sx={{
          fontSize: { xs: 12, sm: 13 },
          fontWeight: 600,
          color: 'rgba(33, 33, 33, 0.7)',
          mt: 0.25,
          fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        {branchName}
      </Typography>
    </Box>
  </Box>
);

// ЗВЁЗДЫ ДЛЯ КНОПКИ
const Stars: React.FC<{ count: number; color: string }> = ({ count, color }) => (
  <span style={{ color, fontSize: 20, marginLeft: 4, letterSpacing: -1 }}>
    {Array(count).fill('★').join(' ')}
  </span>
);

// POPUP ОБЁРТКА
const Popup: React.FC<{
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, onClose, children }) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        borderRadius: '20px',
        p: { xs: 2, sm: 3 },
        mx: 2,
        maxWidth: { xs: '100%', sm: '360px' },
        width: '100%',
      },
    }}
  >
    <IconButton
      onClick={onClose}
      sx={{ position: 'absolute', top: 8, right: 8, color: '#8E8E93' }}
    >
      <Close sx={{ fontSize: 20 }} />
    </IconButton>
    {children}
  </Dialog>
);

// ОСНОВНОЙ КОМПОНЕНТ
type Screen = 'rating' | 'feedback' | 'bonusInstructions';

export const ReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { clinic, branches } = useClinic();
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    const photoUrl = getUserPhotoUrl();
    setUserPhotoUrl(photoUrl);
  }, []);

  // Получаем doctorId из URL если есть
  const doctorId = useMemo(() => searchParams.get('doctor'), [searchParams]);

  const currentBranch = branches.find((b) => b.id === user?.branchId);
  const clinicName = clinic?.name || 'клиники';

  // Состояния
  const [screen, setScreen] = useState<Screen>('rating');
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Результат создания отзыва
  const [reviewResult, setReviewResult] = useState<ReviewResponse | null>(null);

  // Попапы
  const [showPositivePopup, setShowPositivePopup] = useState(false);
  const [showNegativeThankPopup, setShowNegativeThankPopup] = useState(false);
  const [showScreenshotPopup, setShowScreenshotPopup] = useState(false);
  const [showOtherPopup, setShowOtherPopup] = useState(false);

  // Экран фидбека (рейтинг 1-3)
  const [checkedReasons, setCheckedReasons] = useState<Set<string>>(new Set());
  const [otherText, setOtherText] = useState('');
  const [otherDraft, setOtherDraft] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Скриншот
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ОТПРАВКА ОЦЕНКИ
  const handleSubmitRating = async () => {
    if (!selectedRating || loading) return;
    setLoading(true);
    try {
      // Собираем review text для низких оценок (если уже есть фидбек)
      const result = await reviewService.createReview(selectedRating, undefined, doctorId || undefined);
      setReviewResult(result);

      if (selectedRating >= GOOD_THRESHOLD) {
        if (result.nextReviewLink) {
          setScreen('bonusInstructions');
        } else {
          setShowPositivePopup(true);
        }
      } else {
        setScreen('feedback');
      }
    } catch (e) {
      console.error('Ошибка при отправке отзыва:', e);
    } finally {
      setLoading(false);
    }
  };

  // ОТПРАВКА ФИДБЕКА (ПРИЧИНЫ)
  const handleSubmitFeedback = async () => {
    if (feedbackLoading) return;
    setFeedbackLoading(true);
    try {
      const reasons = Array.from(checkedReasons);
      // Заменяем "Другое" на текст если есть
      const reviewText = reasons
        .map((r) => (r === 'Другое' && otherText ? otherText : r))
        .join('; ');

      // Обновляем отзыв с текстом причин
      if (reviewResult?.review?.id) {
        await reviewService.createReview(selectedRating!, reviewText, doctorId || undefined);
      }
      setShowNegativeThankPopup(true);
    } catch (e) {
      console.error('Ошибка при отправке фидбека:', e);
    } finally {
      setFeedbackLoading(false);
    }
  };

  // ЗАГРУЗКА СКРИНШОТА
  const handleScreenshotSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !reviewResult?.review?.id) return;
    setScreenshotLoading(true);
    try {
      await reviewService.uploadScreenshot(reviewResult.review.id, file);
      setShowScreenshotPopup(true);
    } catch (err) {
      console.error('Ошибка при загрузке скриншота:', err);
    } finally {
      setScreenshotLoading(false);
    }
  };

  // ВЫБОР ПРИЧИНЫ
  const toggleReason = (reason: string) => {
    if (reason === 'Другое') {
      if (checkedReasons.has('Другое')) {
        const next = new Set(checkedReasons);
        next.delete('Другое');
        setCheckedReasons(next);
        setOtherText('');
      } else {
        setOtherDraft(otherText);
        setShowOtherPopup(true);
      }
      return;
    }
    const next = new Set(checkedReasons);
    if (next.has(reason)) next.delete(reason);
    else next.add(reason);
    setCheckedReasons(next);
  };

  const handleOtherSave = () => {
    if (otherDraft.trim()) {
      setOtherText(otherDraft.trim());
      const next = new Set(checkedReasons);
      next.add('Другое');
      setCheckedReasons(next);
    }
    setShowOtherPopup(false);
  };

  // РЕНДЕР ЭКРАНА РЕЙТИНГА
  const renderRatingScreen = () => (
    <>
      <BackButton onClick={() => navigate(-1)} />
      <Typography sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 700, color: '#000', mb: 1, lineHeight: 1.1 }}>
        Оставить отзыв
      </Typography>

      <UserCard
        name={user?.name || 'Пользователь'}
        branchName={currentBranch?.name || 'Филиал не выбран'}
        onEdit={() => navigate('/profile')}
        photoUrl={userPhotoUrl}
      />

      {/* Плитка с текстом и оценками */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          p: { xs: 2, sm: 2.5 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 20, sm: 24 },
            fontWeight: 600,
            color: '#1C1C1E',
            mb: 1,
            lineHeight: 1.2,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          Спасибо, что хотите оставить отзыв!
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 500,
            color: '#6B6B6F',
            mb: 2,
            lineHeight: 1.4,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          Пожалуйста, оцените посещение по шкале от 1 до 5, где 1 - это плохо,<br />
          а 5 - отлично
        </Typography>

        {/* Кнопки рейтинга */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 }, mb: 0 }}>
        {[1, 2, 3, 4, 5].map((n) => {
          const isSelected = selectedRating === n;
          return (
              <Box
                key={n}
                onClick={() => setSelectedRating(n)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  py: { xs: 0.5, sm: 0.6 },
                  px: { xs: 1, sm: 1.5 },
                  borderRadius: '22px',
                  border: '1.5px solid',
                  borderColor: isSelected ? '#1677FF' : '#212121',
                  backgroundColor: isSelected ? '#1677FF' : '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:active': { transform: 'scale(0.95)' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: 13, sm: 15 },
                    fontWeight: 500,
                    color: isSelected ? '#FFFFFF' : '#212121',
                    fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  }}
                >
                  {n}
                </Typography>
                <Stars count={n} color={isSelected ? '#FFFFFF' : '#FFCC00'} />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Кнопка отправить */}
      <Button
        fullWidth
        variant="contained"
        disabled={!selectedRating || loading}
        onClick={handleSubmitRating}
        sx={{
          backgroundColor: selectedRating ? '#007AFF' : '#E0E0E0',
          color: selectedRating ? '#FFFFFF' : '#8E8E93',
          borderRadius: '14px',
          height: { xs: 44, sm: 48 },
          fontWeight: 600,
          fontSize: { xs: 14, sm: 16 },
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { backgroundColor: selectedRating ? '#006AE0' : '#E0E0E0' },
          '&.Mui-disabled': {
            backgroundColor: '#E0E0E0',
            color: '#8E8E93',
          },
        }}
      >
        {loading ? <CircularProgress size={20} sx={{ color: '#FFF' }} /> : 'Отправить'}
      </Button>
    </>
  );

  // РЕНДЕР ЭКРАНА ФИДБЕКА (РЕЙТИНГ 1-3)
  const renderFeedbackScreen = () => (
    <>
      <BackButton onClick={() => setScreen('rating')} />
      <Typography sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 700, color: '#000', mb: 1, lineHeight: 1.1 }}>
        Благодарим вас за отзыв!
      </Typography>
      <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: '#8E8E93', mb: 2, lineHeight: 1.3 }}>
        Уточните, пожалуйста, в чём была причина такой оценки? Укажите из списка и нажмите кнопку
        «Oтправить» или нажмите «Другое», если вашего варианта нет в списке, и опишите его своими
        словами
      </Typography>

      {/* Чекбоксы */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
        {NEGATIVE_REASONS.map((reason) => {
          const isOther = reason === 'Другое';
          const displayText = isOther && otherText ? otherText : reason;
          const isChecked = checkedReasons.has(reason);

          return (
            <Box
              key={reason}
              onClick={() => toggleReason(reason)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2, // 👈 ДОБАВЛЕНО: внутренние отступы слева и справа
                borderRadius: '14px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E8E8E8',
                cursor: 'pointer',
                '&:active': { backgroundColor: '#F8F8F8' },
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  color: '#212121',
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mr: 1, // отступ справа от текста до чекбокса
                }}
              >
                {displayText}
              </Typography>
              <Checkbox
                checked={isChecked}
                sx={{
                  p: 0,
                  color: '#D0D0D0',
                  ml: 1, // 👈 ДОБАВЛЕНО: отступ слева от чекбокса (опционально)
                  '&.Mui-checked': { color: '#007AFF' },
                }}
              />
            </Box>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        disabled={checkedReasons.size === 0 || feedbackLoading}
        onClick={handleSubmitFeedback}
        sx={{
          backgroundColor: checkedReasons.size > 0 ? '#007AFF' : '#E0E0E0',
          color: checkedReasons.size > 0 ? '#FFFFFF' : '#8E8E93',
          borderRadius: '14px',
          height: 48,
          fontWeight: 600,
          fontSize: 16,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: checkedReasons.size > 0 ? '#006AE0' : '#E0E0E0',
          },
          '&.Mui-disabled': {
            backgroundColor: '#E0E0E0',
            color: '#8E8E93',
          },
        }}
      >
        {feedbackLoading ? <CircularProgress size={24} sx={{ color: '#FFF' }} /> : 'Отправить'}
      </Button>
    </>
  );

  // РЕНДЕР ЭКРАНА БОНУСНЫХ ИНСТРУКЦИЙ (РЕЙТИНГ 4-5 + nextReviewLink)
  const renderBonusScreen = () => {
    const link = reviewResult?.nextReviewLink;
    return (
    <>
        <BackButton onClick={() => navigate(-1)} />
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#000', mb: 1, lineHeight: 1.1 }}>
          Благодарим за вашу оценку и выбор нашего медицинского центра {clinicName}
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#8E8E93', mb: 1.5, lineHeight: 1.3 }}>
          Чтобы получить 200 бонусных баллов, пожалуйста, оставьте отзыв на картах по кнопке ниже,
          а затем выполните несколько простых шагов:
        </Typography>

        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#007AFF', mb: 1 }}>
          Инструкция для начисления баллов:
        </Typography>
        <Box component="ol" sx={{ pl: 2.5, mb: 3, '& li': { fontSize: 14, color: '#212121', mb: 0.5 } }}>
          <li>Сделайте скриншот вашего опубликованного отзыва на картах</li>
          <li>Вернитесь сюда и нажмите кнопку «Прикрепить скриншот»</li>
          <li>Отправьте скриншот нам</li>
          <li>Дождитесь проверки</li>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            if (link?.url) window.open(link.url, '_blank');
          }}
          sx={{
            borderRadius: '14px',
            height: 48,
            fontWeight: 600,
            fontSize: 15,
            textTransform: 'none',
            borderColor: '#E0E0E0',
            color: '#212121',
            mb: 1.5,
            '&:hover': { borderColor: '#007AFF', color: '#007AFF' },
          }}
        >
          ✏️ Оставить отзыв
        </Button>

        <Button
          fullWidth
          variant="contained"
          disabled={screenshotLoading}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            backgroundColor: '#007AFF',
            borderRadius: '14px',
            height: 48,
            fontWeight: 600,
            fontSize: 15,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#006AE0' },
          }}
        >
          {screenshotLoading ? (
            <CircularProgress size={24} sx={{ color: '#FFF' }} />
          ) : (
            '📎 Прикрепить скриншот'
          )}
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleScreenshotSelect}
        />
      </>
    );
  };

  // РЕНДЕР
  return (
    <AppLayout title="Оставить отзыв">
      <Box sx={{ pb: 8, px: 1 }}>
        {screen === 'rating' && renderRatingScreen()}
        {screen === 'feedback' && renderFeedbackScreen()}
        {screen === 'bonusInstructions' && renderBonusScreen()}
      </Box>

      {/* ========== ПОПАП: Положительный отзыв (4-5, без бонусной ссылки) ========== */}
      <Popup open={showPositivePopup} onClose={() => { setShowPositivePopup(false); navigate(-1); }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#000', mb: 1, pr: 3, lineHeight: 1.1 }}>
          Благодарим за вашу оценку и выбор нашего медицинского центра {clinicName}
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#8E8E93', mb: 2, lineHeight: 1.3 }}>
          Для нас очень важно мнение наших пациентов. Будем рады видеть вас снова!
        </Typography>
        <Button
          fullWidth
          variant="contained"
          onClick={() => { setShowPositivePopup(false); navigate(-1); }}
          sx={{
            backgroundColor: '#007AFF',
            borderRadius: '14px',
            height: 48,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
          }}
        >
          Закрыть
        </Button>
      </Popup>

      {/* ========== ПОПАП: Спасибо за фидбек (после отправки причин 1-3) ========== */}
      <Popup open={showNegativeThankPopup} onClose={() => { setShowNegativeThankPopup(false); navigate(-1); }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#000', mb: 1, pr: 3, lineHeight: 1.1 }}>
          Спасибо за ваш ответ!
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#8E8E93', mb: 2, lineHeight: 1.3 }}>
          Мы обязательно разберёмся в ситуации и сделаем выводы, чтобы подобное не повторялось.
          Будем ждать новой возможности помочь вам!
        </Typography>
        <Button
          fullWidth
          variant="contained"
          onClick={() => { setShowNegativeThankPopup(false); navigate(-1); }}
          sx={{
            backgroundColor: '#007AFF',
            borderRadius: '14px',
            height: 48,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
          }}
        >
          Закрыть
        </Button>
      </Popup>

      {/* ========== ПОПАП: Скриншот получен ========== */}
      <Popup open={showScreenshotPopup} onClose={() => { setShowScreenshotPopup(false); navigate(-1); }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#000', mb: 1, pr: 3, lineHeight: 1.1 }}>
          Отлично, скриншот получен!
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#8E8E93', mb: 2, lineHeight: 1.3 }}>
          Как только проверим отзыв — 200 бонусных баллов зачислятся на ваш счёт.
        </Typography>
        <Button
          fullWidth
          variant="contained"
          onClick={() => { setShowScreenshotPopup(false); navigate(-1); }}
          sx={{
            backgroundColor: '#007AFF',
            borderRadius: '14px',
            height: 48,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
          }}
        >
          Закрыть
        </Button>
      </Popup>

      {/* ========== ПОПАП: Ввод текста «Другое» ========== */}
      <Popup open={showOtherPopup} onClose={() => setShowOtherPopup(false)}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#000', mb: 1.5, pr: 3, lineHeight: 1.1 }}>
          Опишите, пожалуйста, ситуацию максимально подробно
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder="Введите текст"
          value={otherDraft}
          onChange={(e) => setOtherDraft(e.target.value)}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              height: 'auto',
              backgroundColor: '#F8F8F8',
            },
          }}
        />
        <Button
          fullWidth
          variant="contained"
          onClick={handleOtherSave}
          sx={{
            backgroundColor: '#007AFF',
            borderRadius: '14px',
            height: 48,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
          }}
        >
          {otherDraft.trim() ? 'Готово' : 'Закрыть'}
        </Button>
      </Popup>
    </AppLayout>
  );
};
